import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MembershipContext = createContext(null);

export const MembershipProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMembership = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/membership/status`);
      setMembership(response.data);
    } catch (error) {
      console.error('Failed to fetch membership:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  // Check if user has access to a specific feature
  const hasFeature = useCallback((featureKey) => {
    if (!membership?.features) return false;
    return membership.features[featureKey] === true;
  }, [membership]);

  // Check feature and show upgrade prompt if not available
  const checkFeature = useCallback((featureKey, featureName) => {
    if (hasFeature(featureKey)) return true;
    
    const tierRequired = featureKey === 'incognito_mode' || 
                        featureKey === 'unsend_messages' || 
                        featureKey === 'unlimited_taps' ||
                        featureKey === 'unlimited_grid' ||
                        featureKey === 'passport_mode' ||
                        featureKey === 'boost' ? 'Elite' : 'Premium';
    
    toast.error(`${featureName || featureKey} is a ${tierRequired} feature. Upgrade to unlock!`);
    return false;
  }, [hasFeature]);

  // Get remaining limits
  const getLimits = useCallback(() => {
    if (!membership?.limits) {
      return {
        tapsRemaining: 10,
        tapsLimit: 10,
        gridLimit: 50,
        expiringPhotosRemaining: 3,
        expiringPhotosLimit: 3
      };
    }
    
    return {
      tapsRemaining: membership.limits.taps_remaining,
      tapsLimit: membership.limits.taps_per_day,
      gridLimit: membership.limits.grid_limit,
      expiringPhotosRemaining: Math.max(0, 
        membership.limits.expiring_photos_per_day - membership.limits.expiring_photos_used_today
      ),
      expiringPhotosLimit: membership.limits.expiring_photos_per_day
    };
  }, [membership]);

  // Get current tier
  const getTier = useCallback(() => {
    return membership?.tier || 'free';
  }, [membership]);

  // Check if user is premium or elite
  const isPremium = useCallback(() => {
    const tier = getTier();
    return tier === 'premium' || tier === 'elite';
  }, [getTier]);

  const isElite = useCallback(() => {
    return getTier() === 'elite';
  }, [getTier]);

  // Refresh membership data (call after actions that change limits)
  const refresh = useCallback(() => {
    fetchMembership();
  }, [fetchMembership]);

  return (
    <MembershipContext.Provider value={{
      membership,
      loading,
      hasFeature,
      checkFeature,
      getLimits,
      getTier,
      isPremium,
      isElite,
      refresh
    }}>
      {children}
    </MembershipContext.Provider>
  );
};

export const useMembership = () => {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within a MembershipProvider');
  }
  return context;
};

export default MembershipContext;
